import { useState } from "react";
import { ChevronDown } from "lucide-react";
import type { components } from "@/api/schema";
import { Button } from "@/components/ui/button";
import { SourceEmailLink } from "@/features/manufacturer-data/source-email-link";
import { cn, formatEventTime, parseUTCDate } from "@/lib/utils";

export type DiscountChange =
  components["schemas"]["ManufacturerDiscountChangeRead"];

const COLLAPSED_COUNT = 8;

// Jobs that change discounts, by the name they record.
const JOB_NAMES: Record<string, string> = {
  "aq-export-import": "AQ export",
  "cfs-price-list-import": "CFS price list",
};

function changedBy(change: DiscountChange): string | null {
  return (
    change.changed_by_user?.name ||
    (change.changed_by ? (JOB_NAMES[change.changed_by] ?? null) : null)
  );
}

function shortDate(value: string) {
  const date = parseUTCDate(value);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === new Date().getFullYear()
      ? {}
      : { year: "numeric" }),
  });
}

function Chain({ value }: { value: string | null | undefined }) {
  return value ? (
    <span className="font-mono tabular-nums">{value}</span>
  ) : (
    <span>no discount</span>
  );
}

function Description({ change }: { change: DiscountChange }) {
  if (change.action === "added") {
    return (
      <>
        Added at <Chain value={change.discount} />
      </>
    );
  }
  if (change.action === "removed") {
    return (
      <>
        Removed, was <Chain value={change.previous_discount} />
      </>
    );
  }
  const chainChanged = change.previous_discount !== change.discount;
  const renamed = change.previous_name !== change.name;
  return (
    <>
      {chainChanged && (
        <>
          <Chain value={change.previous_discount} /> →{" "}
          <Chain value={change.discount} />
        </>
      )}
      {chainChanged && renamed && " · "}
      {renamed && <>Renamed from {change.previous_name || "unnamed"}</>}
    </>
  );
}

/** A list of discount changes, newest first; `showName` labels each with its discount. */
export function DiscountChangeList({
  changes,
  showName = false,
  label,
}: {
  changes: DiscountChange[];
  showName?: boolean;
  label: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? changes : changes.slice(0, COLLAPSED_COUNT);
  return (
    <div>
      <ol className="space-y-2" aria-label={label}>
        {visible.map((change) => {
          const who = changedBy(change);
          return (
            <li key={change.id} className="text-xs">
              <p className="break-words">
                {showName && (
                  <span className="font-medium">
                    {change.name || "Unnamed category"}:{" "}
                  </span>
                )}
                <Description change={change} />
              </p>
              <p className="text-muted-foreground">
                <time dateTime={change.created_at} className="tabular-nums">
                  {formatEventTime(change.created_at)}
                </time>
                {who && ` · ${who}`}
                {change.source_email && (
                  <>
                    {" · "}
                    <SourceEmailLink source={change.source_email} />
                  </>
                )}
              </p>
              {change.note && (
                <p className="mt-0.5 break-words text-muted-foreground">
                  “{change.note}”
                </p>
              )}
            </li>
          );
        })}
      </ol>
      {changes.length > COLLAPSED_COUNT && (
        <Button
          variant="link"
          size="sm"
          className="mt-1 h-auto px-0 text-xs text-muted-foreground"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? "Show less" : `Show all ${changes.length}`}
        </Button>
      )}
    </div>
  );
}

/** "Updated Oct 21 · Max" under a discount; opens that discount's history in place. */
export function DiscountLastUpdated({
  changes,
}: {
  changes: DiscountChange[];
}) {
  const [open, setOpen] = useState(false);
  const latest = changes[0];
  if (!latest) return null;
  const who = changedBy(latest);
  const verb = latest.action === "added" ? "Added" : "Updated";
  return (
    <div className="mt-0.5">
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-sm text-xs text-muted-foreground hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span>
          {verb}{" "}
          <time dateTime={latest.created_at} className="tabular-nums">
            {shortDate(latest.created_at)}
          </time>
          {who && ` · ${who}`}
        </span>
        <ChevronDown
          className={cn("size-3 transition-transform", open && "rotate-180")}
          aria-hidden
        />
      </button>
      {open && (
        <div className="mt-2 border-l pl-3">
          <DiscountChangeList
            changes={changes}
            label={`${latest.name || "Discount"} history`}
          />
        </div>
      )}
    </div>
  );
}
